import * as React from "react";
import { X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { getStaffingClientsWithFilters } from "@/services/client/Client";
import { useApiClient } from "@/context/ApiContext";

interface ClientOption {
    id: number;
    name: string;
    region: string | null;
}

interface StaffingClientPickerProps {
    label?: string;
    required?: boolean;
    onChange: (clientIds: number[]) => void;
}

export function StaffingClientPicker({ label, required, onChange }: StaffingClientPickerProps) {
    const apiClient = useApiClient();
    const [searchValue, setSearchValue] = React.useState("");
    const [selected, setSelected] = React.useState<ClientOption[]>([]);
    const [suggestions, setSuggestions] = React.useState<ClientOption[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isOpen, setIsOpen] = React.useState(false);
    const containerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const search = React.useCallback(
        async (text: string) => {
            if (text.length < 2) {
                setSuggestions([]);
                setIsOpen(false);
                return;
            }
            setIsLoading(true);
            setIsOpen(true);
            try {
                const res = await getStaffingClientsWithFilters(
                    { filter: `contains(Name,'${text}')`, select: ["Id", "Name", "Region"], top: 10 },
                    apiClient
                );
                const clients: { Id: number; Name: string | null; Region: string | null }[] =
                    res.data.value ?? res.data;
                setSuggestions(
                    clients
                        .map((c) => ({ id: c.Id, name: c.Name ?? String(c.Id), region: c.Region }))
                        .filter((c) => !selected.some((s) => s.id === c.id))
                );
            } catch {
                setSuggestions([]);
            } finally {
                setIsLoading(false);
            }
        },
        [apiClient, selected]
    );

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchValue(value);
        search(value);
    };

    const onSelect = (client: ClientOption) => {
        const next = [...selected, client];
        setSelected(next);
        setSearchValue("");
        setSuggestions([]);
        setIsOpen(false);
        onChange(next.map((c) => c.id));
    };

    const onRemove = (id: number) => {
        const next = selected.filter((c) => c.id !== id);
        setSelected(next);
        onChange(next.map((c) => c.id));
    };

    const showDropdown = isOpen && (isLoading || suggestions.length > 0 || searchValue.length >= 2);

    return (
        <div className="flex flex-col gap-2">
            {label && (
                <Label>
                    {label}
                    {required && <span className="text-destructive ml-0.5"> *</span>}
                </Label>
            )}

            <div ref={containerRef} className="relative">
                <Input
                    placeholder="Type client name..."
                    value={searchValue}
                    onChange={onInputChange}
                />
                {showDropdown && (
                    <div className="absolute z-50 w-full bg-popover border border-border rounded-md shadow-md max-h-[200px] overflow-y-auto top-full mt-1">
                        {isLoading && (
                            <div className="p-2 text-sm text-muted-foreground">Loading...</div>
                        )}
                        {!isLoading && suggestions.length === 0 && searchValue.length >= 2 && (
                            <div className="p-2 text-sm text-muted-foreground">No results found</div>
                        )}
                        {suggestions.map((client) => (
                            <button
                                key={client.id}
                                type="button"
                                className="w-full flex flex-col gap-0.5 p-2 text-left hover:bg-muted text-sm cursor-pointer border-none bg-transparent"
                                onClick={() => onSelect(client)}
                            >
                                <span className="font-semibold">{client.name}</span>
                                <span className="text-xs text-muted-foreground">
                                    ID: {client.id}{client.region ? ` · ${client.region}` : ""}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {selected.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                    {selected.map((client) => (
                        <Badge key={client.id} variant="secondary" className="gap-1 pr-1">
                            <span>{client.name}</span>
                            <button
                                type="button"
                                className="ml-1 rounded-full hover:bg-muted-foreground/20 p-0.5"
                                onClick={() => onRemove(client.id)}
                                aria-label={`Remove ${client.name}`}
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </Badge>
                    ))}
                </div>
            )}
        </div>
    );
}
